import React, { useEffect, useMemo, useState } from 'react';
import { Typography, TextField, Alert, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SourceContentProps } from './types';
import {
  analyzeStatementXmlSegments,
  StatementXmlEditor,
  serializeStatementXmlSegments,
  type StatementSegment,
} from '../statement-nodes';
import { StatementXmlPreview } from '../statement-nodes/StatementXmlPreview';

export const SourceContent: React.FC<SourceContentProps> = ({
  sourceText,
  sourceLanguage,
  sourceLanguageDisplay,
  isExistingProduct,
  onSourceChange,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [xmlSegments, setXmlSegments] = useState<StatementSegment[] | null>(null);
  const sourceAnalysis = useMemo(() => analyzeStatementXmlSegments(sourceText), [sourceText]);
  const displaySourceText = sourceAnalysis.hasMissingGlossaryFields ? sourceAnalysis.plainText : sourceText;

  const hasExistingSourceText = isExistingProduct && displaySourceText.trim().length > 0;

  useEffect(() => {
    setXmlSegments(sourceAnalysis.hasNodes ? sourceAnalysis.segments : null);
  }, [sourceAnalysis]);

  const header = (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {t('translation.source')} · {sourceLanguageDisplay}
      </Typography>
    </Box>
  );

  // Read-only display — reuse the same preview component used on the product page
  if (disabled) {
    return (
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          p: 2,
          backgroundColor: 'background.paper',
        }}
      >
        {header}
        {xmlSegments ? (
          <StatementXmlPreview value={sourceText} layout="codesAndSentences" />
        ) : (
          <Typography
            variant="body2"
            color={displaySourceText ? 'text.primary' : 'text.disabled'}
            sx={{ lineHeight: 1.7, whiteSpace: 'pre-wrap' }}
          >
            {displaySourceText || t('translation.sourcePlaceholderExisting', { language: sourceLanguageDisplay })}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 2,
        backgroundColor: 'background.paper',
      }}
    >
      {header}

      {hasExistingSourceText && (
        <Alert severity="info" sx={{ mb: 2 }} variant="outlined">
          {t('translation.usingExistingSource', { language: sourceLanguageDisplay })}
        </Alert>
      )}

      {xmlSegments ? (
        <StatementXmlEditor
          segments={xmlSegments}
          language={sourceLanguage}
          onSegmentsChange={(next) => {
            setXmlSegments(next);
            onSourceChange(serializeStatementXmlSegments(next));
          }}
          disabled={false}
        />
      ) : (
        <TextField
          value={displaySourceText}
          onChange={(e) => onSourceChange(e.target.value)}
          fullWidth
          multiline
          minRows={8}
          maxRows={16}
          placeholder={
            isExistingProduct
              ? t('translation.sourcePlaceholderExisting', { language: sourceLanguageDisplay })
              : t('translation.sourcePlaceholderNew')
          }
          helperText={
            isExistingProduct && !hasExistingSourceText
              ? t('translation.noExistingSource', { language: sourceLanguageDisplay })
              : undefined
          }
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              fontFamily: 'monospace',
              fontSize: '0.85rem',
              lineHeight: 1.7,
              backgroundColor: '#fafafa',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'divider',
            },
          }}
        />
      )}
    </Box>
  );
};
